import { lazy, useCallback } from 'react';
import {
  Container, Toolbar, Typography, Button as MuiButton,
  useScrollTrigger,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { SignupIntents, ProfileTypes } from '@sda/enums';
import { useIsMobile } from '@client/hooks/dom.hooks';
import { Modal, Prompt } from '@client/components/globals/Modal';
import { useDispatch, useListeners, useSelector } from '@client/hooks/redux.hooks';
import { artistFormListeners } from '@client/components/Main/Profiles/Act/Form/artist-form.listeners';
import { hostFormListeners } from '@client/components/Main/Profiles/Venue/Form/host-form.listeners';
import { getSelectedProfileType, getSelectedProfileIsCompleted, getSelectedProfileIsVetted } from '@client/selectors/helpers/profile';
import { getSessionUserState } from '@client/selectors/helpers/login';
import { openSignupModal } from '@client/components/Main/Signup';
import { showSnackbar } from '@client/listeners/lib/snackbar';
import { startCreateShowCallThread } from '@client/slices/entities/threads.reducer';
import { Loader } from '@client/components/common/Loader';
import { MessageSchema } from './message.schema';
import { MessageModal } from './MessageModal';
import { ManageProfileButton } from './ManageProfileButton';
import type { Beacon } from '@sda/interfaces/beacon';
import type { FetchDataType } from '@sda/sdk/interfaces';

const useStyles = makeStyles({ name: 'CTABar' })((theme) => ({
  root: {
    backgroundColor: theme.palette.primary.xlight,
    borderBottomColor: theme.palette.primary.light,
    borderBottomStyle: 'solid',
    borderBottomWidth: 1,
    position: 'sticky',
    top: 0,
    zIndex: theme.zIndex.appBar + 1,
    width: '100%',
    transition: theme.transitions.create('top', {
      delay: 0,
      duration: 225,
      easing: theme.transitions.easing.easeOut,
    }),

    [theme.breakpoints.down('md')]: {
      borderTopColor: theme.palette.primary.light,
      borderTopStyle: 'solid',
      borderTopWidth: 1,
      marginTop: -1, // Override the default border of the app bar
    },
  },
  scroll: {
    [theme.breakpoints.down('md')]: {
      top: theme.spacing(8),
    },
  },
  container: {
    paddingBottom: '0px !important',

    [theme.breakpoints.up('md')]: {
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
  toolbar: {
    minHeight: theme.spacing(6),

    [theme.breakpoints.up('md')]: {
      minHeight: theme.spacing(7),
    },
  },
  label: {
    color: theme.palette.primary.dark,
  },
}));

const FinishProfileModalLoader = lazy(() => import('./FinishProfileModal'));
const VetProfileModalLoader = lazy(() => import('@client/components/routes/Connect/common/VetProfileModal'));

export const CTABar: React.FC<Beacon & FetchDataType> = ({ ...call }) => {
  const dispatch = useDispatch();

  const { classes, cx } = useStyles();
  const isMobile = useIsMobile();
  const trigger = useScrollTrigger();

  useListeners(artistFormListeners, hostFormListeners);

  const userState = useSelector(getSessionUserState);
  const isCompleted = useSelector(getSelectedProfileIsCompleted);
  const isVetted = useSelector(getSelectedProfileIsVetted);
  const selectedProfileType = useSelector(getSelectedProfileType);
  const isArtistBeacon = call.profileType === ProfileTypes.ACT;

  const handleMessageSend = useCallback(({ message }) => {
    dispatch(startCreateShowCallThread({ beaconId: call.id, message }));
  }, [call.id, dispatch]);

  const handleClick = useCallback(() => {
    if (!userState) {
      showSnackbar('Please sign up to respond to a Show Call.', 'warning');

      openSignupModal({
        destination: `/connect/${call.id}/${isArtistBeacon ? 'host' : 'artist'}/signup`,
        intent: isArtistBeacon ? SignupIntents.VENUE : SignupIntents.ARTIST,
      });
    } else if (!isCompleted) {
      Modal.show({
        backgroundColor: 'primary',
        title: ' ',
        buttons: [],
        body: (
          <Loader
            load={FinishProfileModalLoader}
            beaconId={call.id}
          />
        ),
      });
    } else if (!isVetted) {
      Modal.show({
        backgroundColor: 'primary',
        title: ' ',
        buttons: [],
        body: (
          <Loader load={VetProfileModalLoader} />
        ),
      });
    } else {
      Prompt.show({
        buttonLabel: 'Send Message',
        ButtonProps: { fullWidth: true, size: 'large' },
        onSubmit: handleMessageSend,
        scope: 'send-showcall-message',
        title: 'Send a Message',
        validationSchema: MessageSchema,
        component: (
          <MessageModal {...call} />
        ),
        initialValues: {
          message: '',
        },
      });
    }
  }, [call, isArtistBeacon, handleMessageSend, userState, isCompleted, isVetted]);

  return (
    <div className={cx(classes.root, !trigger && classes.scroll)}>
      <Container
        className={classes.container}
        maxWidth="narrow"
      >
        <Toolbar
          className={classes.toolbar}
          disableGutters
        >
          <Typography
            className={classes.label}
            variant={isMobile ? 'overline' : 'body2'}
          >
            {isArtistBeacon ? 'Want to host this artist?' : 'Want to perform here?'}
          </Typography>

          <div className="spacer" />

          {!userState || (selectedProfileType && selectedProfileType !== call.profileType) ? (
            <MuiButton
              color="primary"
              size={isMobile ? 'small' : 'medium'}
              variant="contained"
              onClick={handleClick}
            >
              Send a Message
            </MuiButton>
          ) : (
            <ManageProfileButton
              beaconId={call.id}
              beaconProfileType={call.profileType}
            />
          )}
        </Toolbar>
      </Container>
    </div>
  );
};
